import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Header from "components/Header/Header.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import Footer from "components/Footer/Footer.js";
import logo from 'assets/img/logo.png';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, updateProfile, getIdToken } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import TextField from "@material-ui/core/TextField";
import { CourseFeatures } from './components/CourseFeatures';
import { InstructorSection } from './components/InstructorSection';
import { CourseDescription } from './components/CourseDescription';
import TestimonialCard from './components/TestimonialCard';
import { ActionButton } from './components/ActionButton';
import starIcon from 'assets/img/star.svg';
import Typography from "@material-ui/core/Typography";
import { BonusSection, FullBonusSection } from './components/BonusSection';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../../contexts/AuthContext';
import { InputAdornment, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { List, ListItem } from '@material-ui/core';
import { Favorite } from '@material-ui/icons';
import { defaultContent, defaultStyles } from '../AdminPage/components/SalesPageCustomizer';

// Initialize Stripe with the correct environment variable name and fallback
const stripePromise = loadStripe(window.env?.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_KEY);

// Add these testimonial data objects at the top level
const defaultTestimonials = [
  {
    videoUrl: "https://www.youtube.com/watch?v=example1",
    thumbnail: "https://example.com/thumbnail1.jpg"
  },
  {
    videoUrl: "https://www.youtube.com/watch?v=example2",
    thumbnail: "https://example.com/thumbnail2.jpg"
  },
  {
    videoUrl: "https://www.youtube.com/watch?v=example3",
    thumbnail: "https://example.com/thumbnail3.jpg"
  }
];

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: props => {
      const styles = props.customStyles || defaultStyles;
      
      switch (styles.backgroundType) {
        case 'gradient':
          return `linear-gradient(180deg, ${styles.gradientStart} 0%, ${styles.gradientEnd} 100%)`;
        case 'solid':
          return styles.backgroundColor;
        case 'image':
          return `url(${styles.backgroundImage})`;
        default:
          return 'linear-gradient(180deg, #f00 0%, #900 100%)';
      }
    },
    backgroundSize: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.backgroundType === 'image' ? styles.backgroundSize : 'auto';
    },
    backgroundPosition: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.backgroundType === 'image' ? styles.backgroundPosition : 'auto';
    },
    backgroundRepeat: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.backgroundType === 'image' ? styles.backgroundRepeat : 'auto';
    },
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    position: 'relative',
    overflowX: 'hidden'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.overlayColor;
    },
    opacity: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.overlayOpacity;
    },
    zIndex: 1
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minHeight: '100vh',
    width: '100%',
    padding: '80px 0 40px',
    zIndex: 2,
    overflowX: 'hidden',
    '@media (max-width: 991px)': {
      padding: '60px 0 20px'
    }
  },
  checkoutPage: {
    minHeight: '100vh',
    fontFamily: 'Roboto, sans-serif',
    position: 'relative'
  },
  contentWrapper: {
    display: 'flex',
    padding: '40px 0',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: 'calc(100vh - 100px)',
    overflow: 'hidden'
  },
  checkoutContainer: {
    display: 'flex',
    width: '1368px',
    padding: '20px',
    gap: '20px',
    backgroundColor: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.containerBackgroundColor;
    },
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    height: '100%',
    '@media (max-width: 991px)': {
      flexDirection: 'column',
      width: '90%',
      height: 'auto'
    }
  },
  leftPanel: {
    flex: '1 1 60%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '100%',
    '& > *': {
      maxWidth: '100%',
      wordWrap: 'break-word'
    },
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.3)'
      }
    }
  },
  rightPanel: {
    flex: '1 1 40%',
    position: 'sticky',
    top: '20px',
    maxHeight: 'calc(100vh - 140px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  cardElementContainer: {
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.23)',
    borderRadius: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '20px'
  },
  mobileCta: {
    display: 'none', // Hide by default
    '@media (max-width: 991px)': {
      display: 'block',
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      padding: '10px 20px',
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1000,
      backdropFilter: 'blur(10px)'
    }
  },
  ctaButton: {
    height: '49px',
    background: 'linear-gradient(180deg, #f00 0%, #900 100%)',
    color: '#fff',
    fontWeight: 700,
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    width: '100%',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 0.9
    }
  },
  left: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  list: {
    padding: 0
  },
  inlineBlock: {
    display: 'inline-block'
  },
  block: {
    color: '#fff',
    textDecoration: 'none'
  },
  right: {
    flex: 1,
    textAlign: 'right',
    color: '#fff'
  },
  headerImage: {
    width: '100%',
    maxWidth: '1002px',
    borderRadius: '25px',
    marginTop: '20px',
    marginBottom: '50px',
    aspectRatio: '16/9',
    objectFit: 'cover'
  },
  divider: {
    width: '100%',
    maxWidth: '800px',
    margin: '50px 0',
    border: '3px solid #fff',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
  },
  contentSection: {
    width: '100%',
    maxWidth: '1002px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'  // Center content
  },
  featuresContainer: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    borderRadius: '10px',
    padding: '20px',
    marginTop: '20px'
  },
  testimonialSection: {
    position: 'relative',
    minHeight: '300px',
    marginBottom: '120px',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px 120px'  // Increased bottom padding
  },
  testimonialContainer: {
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '40px'  // Added margin bottom
  },
  testimonialList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    '& > *': {
      position: 'relative !important',
      transform: 'none !important',
      left: 'auto !important',
      top: 'auto !important'
    }
  },
  testimonialRotate: {
    position: 'relative',
    width: '100%',
    maxWidth: '800px',
    height: '300px',
    margin: '0 auto'
  },
  testimonialTitle: {
    color: props => props.customStyles?.textColor || '#ffffff',
    fontSize: '36px',
    fontFamily: '"Roboto Slab", serif',
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: 'center',
    marginBottom: '40px',
    width: '100%'
  },
  mainTitle: {
    color: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.textColors?.title || styles.textColor;
    },
    fontSize: '48px',
    fontWeight: 700,
    marginBottom: '10px',
    fontFamily: '"Roboto Slab", serif',
    width: '100%',
    textAlign: 'center'
  },
  subtitle: {
    color: props => {
      const styles = props.customStyles || defaultStyles;
      return styles.textColors?.subtitle || styles.textColor;
    },
    fontSize: '32px',
    fontWeight: 400,
    marginBottom: '30px',
    fontFamily: '"Roboto Slab", serif',
    width: '100%',
    textAlign: 'center'
  },
  previewSection: {
    width: '100%',
    marginBottom: '30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  videoContainer: {
    width: '100%',
    maxWidth: '1002px',
    aspectRatio: '16/9',
    borderRadius: '25px',
    overflow: 'hidden',
    marginTop: '20px',
    marginBottom: '50px',
    position: 'relative',
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    display: props => props.customStyles?.mediaType === 'video' && props.customStyles?.thumbnail && !props.customStyles?.isPlaying ? 'block' : 'none',
  },
  courseDescriptionSection: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    '& .main-description': {
      textAlign: 'left',
      '& h2': {
        color: '#fff',
        fontSize: '32px',
        fontWeight: 700,
        marginBottom: '20px'
      },
      '& p': {
        color: '#fff',
        fontSize: '18px',
        lineHeight: 1.6,
        marginBottom: '30px'
      }
    },
    '& .what-is': {
      textAlign: 'right',
      marginTop: '40px',
      '& h2': {
        color: '#fff',
        fontSize: '32px',
        fontWeight: 700,
        marginBottom: '20px'
      },
      '& p': {
        color: '#fff',
        fontSize: '18px',
        lineHeight: 1.6,
        marginBottom: '30px'
      }
    },
    '& .ready-to-start': {
      textAlign: 'center',
      marginTop: '40px',
      '& h2': {
        color: '#fff',
        fontSize: '32px',
        fontWeight: 700,
        marginBottom: '20px'
      },
      '& p': {
        color: '#fff',
        fontSize: '18px',
        lineHeight: 1.6,
        marginBottom: '30px'
      }
    }
  },
  badgesSection: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    marginTop: '20px',
    padding: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
  },
  badge: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '15px',
    '& img': {
      width: '48px',
      height: '48px',
      marginBottom: '10px',
    },
    '& h4': {
      margin: '0 0 5px 0',
      fontSize: '16px',
      fontWeight: 600,
      color: '#1a1a1a',
    },
    '& p': {
      margin: 0,
      fontSize: '14px',
      color: '#666',
      lineHeight: 1.4,
    },
  },
  testimonialGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '40px',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    marginBottom: '60px',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
      gap: '30px'
    }
  },
  testimonialVideo: {
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  testimonialHeader: {
    color: props => props.customStyles?.textColor || '#ffffff',
    fontSize: '24px',
    fontFamily: '"Roboto Slab", serif',
    fontWeight: 600,
    textAlign: 'center',
    padding: '0 15px',
    marginTop: '15px'
  },
  testimonialSubtext: {
    color: props => props.customStyles?.textColor || '#ffffff',
    fontSize: '16px',
    fontFamily: '"Roboto", sans-serif',
    lineHeight: 1.6,
    textAlign: 'center',
    padding: '0 15px 15px',
    whiteSpace: 'pre-line'
  },
  videoWrapper: {
    position: 'relative',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    height: 0,
    '& iframe, & video': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '10px'
    }
  },
  testimonialHeading: {
    color: props => props.customStyles?.textColor || '#ffffff',
    fontSize: '36px',
    fontFamily: '"Roboto Slab", serif',
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  },
  testimonialSubtext: {
    color: props => props.customStyles?.textColor || '#ffffff',
    fontSize: '18px',
    fontFamily: '"Roboto", sans-serif',
    lineHeight: 1.6,
    textAlign: 'center',
    marginBottom: '40px',
    width: '100%',
    maxWidth: '800px'
  },
  logo: {
    maxWidth: props => props.customContent?.logo?.maxWidth || '200px',
    height: 'auto',
    marginTop: props => props.customContent?.logo?.marginTop || '20px',
    marginBottom: props => props.customContent?.logo?.marginBottom || '20px',
    objectFit: 'contain'
  },
}));

// StarRating Component
const StarRating = () => {
  return (
    <div className="rating" role="img" aria-label="5 out of 5 stars">
      {[...Array(5)].map((_, index) => (
        <img
          key={index}
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/6d4fe607575203554bef05d00b0563197aa91563"
          alt=""
          className="star"
          aria-hidden="true"
        />
      ))}
      <style>{`
        .rating {
          display: flex;
          gap: 10px;
        }
        .star {
          width: 16px;
          height: 16px;
        }
      `}</style>
    </div>
  );
};

// Update the getYouTubeEmbedUrl function
const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  
  try {
    let baseUrl = '';
    
    // Handle youtu.be format
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        baseUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    }
    // Handle youtube.com format
    else if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        baseUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    }
    // If it's already an embed URL
    else if (url.includes('youtube.com/embed/')) {
      baseUrl = url;
    } else {
      return url;
    }

    // Add autoplay and other parameters if enabled
    const params = new URLSearchParams({
      autoplay: props => props.customStyles?.autoplayVideo ? '1' : '0',
      mute: props => props.customStyles?.autoplayVideo ? '1' : '0', // Required for autoplay
      controls: '1',
      showinfo: '0',
      rel: '0',
      modestbranding: '1',
      playsinline: '1'
    });

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    console.error('Error parsing YouTube URL:', error);
    return url;
  }
};

// Update the CoursePreview component
const CoursePreview = ({ customContent, customStyles }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = customContent?.testimonials || defaultTestimonials;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <section className="preview-section" aria-label="Course Preview">
      <h1 className="title">{customContent?.title || "Meet Girls Everywhere"}</h1>
      <h2 className="subtitle">{customContent?.subtitle || "20+ Modules to Transform Your Dating Life"}</h2>
      <div className="media-container">
        {customContent?.mediaType === 'video' ? (
          <iframe
            src={getYouTubeEmbedUrl(customContent.mediaUrl)}
            title="Course Preview Video"
            className="preview-media"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        ) : (
          <img 
            src={customContent?.mediaUrl || "https://cdn.builder.io/api/v1/image/assets/TEMP/0600b6e10a801aedbbafd987da22c68e37c074a0"} 
            alt="Course Preview" 
            className="preview-media" 
          />
        )}
      </div>
      <div className="testimonials-container">
        {testimonials.map((testimonial, index) => (
          <TestimonialCard
            key={index}
            quote={testimonial.quote}
            username={testimonial.author}
            level={testimonial.level}
            avatar={testimonial.image}
            starIcon={testimonial.starIcon}
            isVisible={index === currentTestimonial}
            align={index % 2 === 0 ? 'left' : 'right'}
          />
        ))}
      </div>
      <style>{`
        .preview-section {
          flex: 1;
          padding: 20px;
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          background-color: rgba(0, 0, 0, 0.1);
          overflow-y: hidden;
          max-height: 100vh;
        }
        .title {
          color: ${customStyles?.textColor || '#fff'};
          font-family: "Roboto Slab", serif;
          font-size: 32px;
          text-align: center;
          margin: 0;
        }
        .subtitle {
          color: ${customStyles?.textColor || '#fff'};
          font-family: "Roboto Slab", serif;
          font-size: 32px;
          text-align: center;
          margin: 0;
        }
        .media-container {
          width: 100%;
          aspect-ratio: 16/9;
          position: relative;
        }
        .preview-media {
          width: 100%;
          height: 100%;
          border-radius: 25px;
          object-fit: cover;
        }
        .testimonials-container {
          position: relative;
          height: 200px;
          overflow: hidden;
        }
        @media (max-width: 991px) {
          .preview-section {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};

// Update the PaymentForm component
const PaymentForm = ({ customContent, isAdmin }) => {
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const auth = getAuth();
  const history = useHistory();
  const { getUserData, userRoles } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState(null);
  const [alreadyOwnsProduct, setAlreadyOwnsProduct] = useState(false);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState({
    applePay: false,
    googlePay: false,
    paypal: false
  });
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    return () => {
      setMounted(false); // Cleanup on unmount
    };
  }, []);

  // Check if user already owns the course and fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        try {
          // Check ownership via context first (faster)
          if (userRoles?.productsOwned?.includes('all-courses')) {
            setAlreadyOwnsProduct(true);
          }
          
          // Always fetch data from Firestore for most up-to-date info
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            console.log('Fetched user data:', data); // Debug log
            
            // Store full user data
            setUserData(data);
            
            // Update form data
            setFormData({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || auth.currentUser.email || '',
            });
            
            // Double-check ownership from Firestore
            if (data.productsOwned && 
               (data.productsOwned.includes('all-courses') || 
                (Array.isArray(data.productsOwned) && 
                 data.productsOwned.some(product => 
                   typeof product === 'object' && 
                   (product.id === 'all-courses' || product.name === 'all-courses')
                 ))
               )
              ) {
              setAlreadyOwnsProduct(true);
            }
          } else {
            console.log('User document not found in Firestore'); // Debug log
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    
    fetchUserData();
  }, [auth.currentUser, userRoles]);

  // Enhanced displayName function
  const getUserDisplayName = () => {
    // First try userData from Firestore
    if (userData) {
      console.log('Using userData for displayName:', userData); // Debug log
      
      // Try firstName + lastName from Firestore
      if (userData.firstName && userData.lastName) {
        return `${userData.firstName}`;
      }
      
      // Try displayName from Firestore
      if (userData.displayName) {
        return userData.displayName;
      }
    }
    
    // Fall back to formData if available
    if (formData.firstName && formData.lastName) {
      return `${formData.firstName} ${formData.lastName}`;
    }
    
    // Last resort: auth displayName or generic greeting
    return auth.currentUser?.displayName || 'there';
  };

  // Initialize payment request for digital wallets
  useEffect(() => {
    let paymentRequestObj = null;
    
    const initializePaymentRequest = async () => {
      if (!stripe || !mounted) return;

      try {
        paymentRequestObj = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Meet Girls Everywhere Course',
            amount: isAdmin ? 51 : 29700,
          },
          requestPayerName: true,
          requestPayerEmail: true,
          requestShipping: false,
          paymentMethodTypes: ['card', 'paypal'],
        });

        // Check available payment methods
        const result = await paymentRequestObj.canMakePayment();
        console.log('Payment method check result:', result);
        console.log('Current hostname:', window.location.hostname);
        console.log('Protocol:', window.location.protocol);
        
        if (mounted) {
          setAvailablePaymentMethods({
            applePay: !!result?.applePay,
            googlePay: !!result?.googlePay,
            paypal: true
          });
          
          if (result) {
            setPaymentRequest(paymentRequestObj);
          }
        }
      } catch (error) {
        console.error('Error initializing payment request:', error);
      }
    };

    initializePaymentRequest();

    return () => {
      paymentRequestObj = null;
    };
  }, [stripe, isAdmin, mounted]);

  const handlePaymentSuccess = async () => {
    try {
      let userId = auth.currentUser?.uid;

      // If user is not logged in, create their account
      if (!auth.currentUser) {
        try {
          // Validate password
          if (formData.password.length < 8) {
            throw new Error('Password must be at least 8 characters');
          }

          // Create the user account with their chosen password
          const { user: newUser } = await createUserWithEmailAndPassword(
            auth, 
            formData.email, 
            formData.password  // Use the password from the form
          );
          userId = newUser.uid;

          // Update the user's profile
          await updateProfile(newUser, {
            displayName: `${formData.firstName} ${formData.lastName}`.trim()
          });

          // Create the user document
          const db = getFirestore();
          await setDoc(doc(db, 'users', userId), {
            uid: userId,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            displayName: `${formData.firstName} ${formData.lastName}`.trim(),
            userType: 'student',
            productsOwned: ['all-courses'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });

        } catch (error) {
          console.error('Error creating user account:', error);
          toast.error(error.message || 'Error creating account. Please contact support.');
          return;
        }
      } else {
        // Update existing user's products
        const db = getFirestore();
        await updateDoc(doc(db, 'users', userId), {
          productsOwned: arrayUnion('all-courses'),
          updatedAt: new Date().toISOString()
        });
      }

      // Send confirmation emails
      try {
        const idToken = await auth.currentUser.getIdToken();
        await axios.post(
          `${process.env.REACT_APP_API_URL}/sendEmail`,
          {
            type: 'purchase',
            data: {
              userEmail: formData.email || auth.currentUser.email,
              userName: `${formData.firstName} ${formData.lastName}`.trim(),
              productName: 'Meet Girls Everywhere Course',
              amount: isAdmin ? '$0.51' : '$297',
              isNewUser: !auth.currentUser
            }
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          }
        );
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
      }

      // Refresh user data and redirect
      await getUserData();
      toast.success('Payment successful! Redirecting to courses...');
      history.push('/courses');
    } catch (error) {
      console.error('Error updating user data:', error);
      toast.error('Payment successful but error updating access. Please contact support.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || processing) return;

    setProcessing(true);
    setError(null);

    try {
      // Initialize payment first
      let paymentClientSecret;
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/initPayment`,
          {
            productId: 'c6RF045j7f9zAiQvXV6n',
            isAdmin: isAdmin,
            ...((!auth.currentUser) && {
              guestInfo: {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName
              }
            })
          },
          auth.currentUser ? {
            headers: {
              Authorization: `Bearer ${await auth.currentUser.getIdToken()}`
            }
          } : undefined
        );

        if (!response.data?.clientSecret) {
          throw new Error('Unable to initialize payment. Please try again.');
        }
        
        paymentClientSecret = response.data.clientSecret;
        setClientSecret(paymentClientSecret);
      } catch (error) {
        console.error('Error initializing payment:', error);
        throw new Error('Unable to initialize payment. Please try again.');
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        throw new Error('Card element not found');
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(paymentClientSecret, {
        payment_method: {
          card: card,
          billing_details: {
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            email: formData.email,
          },
        },
      });

      if (error) {
        throw error;
      }

      if (paymentIntent.status === 'succeeded') {
        await handlePaymentSuccess();
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle redirect to courses
  const handleContinueToCourse = () => {
    history.push('/courses');
  };

  return (
    <>
      <div className="payment-wrapper">
        <div className="payment-header">
          <Typography variant="h3" className="header-title">
            {alreadyOwnsProduct 
              ? "You Already Own This Course!" 
              : (customContent?.paymentHeader || "Get Yours Today!")}
          </Typography>
          {alreadyOwnsProduct ? (
            <div className="already-owns-message">
              <Typography variant="body1" style={{ fontSize: '18px', margin: '20px 0', color: '#2e7d32' }}>
                Hi {getUserDisplayName()}, you've already purchased this course. 
                You can continue to your course materials using the button below.
              </Typography>
            </div>
          ) : (
            <div className="price-info">
              <Typography variant="h4" className="price-line">
                <span className="original-price">Total Value: {customContent?.originalPrice || "$997"}</span>
                <span className="current-price">
                  Today Only: {isAdmin ? "$0.51" : customContent?.currentPrice || "$297"}
                </span>
              </Typography>
            </div>
          )}
        </div>

        {alreadyOwnsProduct ? (
          <button
            className="submit-button"
            onClick={handleContinueToCourse}
          >
            Continue to Course
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="payment-form">
            {!auth.currentUser ? (
              <>
                <div className="form-row">
                  <TextField
                    className="form-field"
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    variant="outlined"
                    fullWidth
                  />
                  <TextField
                    className="form-field"
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <TextField
                  className="form-field"
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  variant="outlined"
                  fullWidth
                />
                <TextField
                  className="form-field"
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Password must be at least 8 characters"
                />
                <Typography variant="body2" style={{ marginTop: '-10px', marginBottom: '20px', color: '#666' }}>
                  An account will be created automatically when you complete your purchase
                </Typography>
              </>
            ) : (
              <div className="user-info-display">
                <Typography style={{ marginBottom: '15px', color: '#666' }}>
                  Logged in as: {getUserDisplayName()} ({formData.email || auth.currentUser.email})
                  {isAdmin && (
                    <span style={{ marginLeft: '10px', color: '#ff4444' }}>(Admin Price: $0.51)</span>
                  )}
                </Typography>
              </div>
            )}

            <div className="payment-methods">
              <div className="digital-payment-buttons">
                {(paymentRequest || isAdmin) ? (
                  <>
                    <div className="payment-button-container">
                      {paymentRequest ? (
                        <PaymentRequestButtonElement
                          options={{
                            paymentRequest,
                            style: {
                              paymentRequestButton: {
                                type: 'default',
                                theme: 'dark',
                                height: '40px',
                              },
                            },
                          }}
                        />
                      ) : isAdmin && (
                        <div className="admin-payment-button disabled">
                          <div className="admin-overlay">Apple/Google Pay (Preview)</div>
                        </div>
                      )}
                    </div>
                    
                    {isAdmin && (
                      <div className="payment-button-container">
                        <button 
                          className="admin-payment-button paypal"
                          onClick={(e) => {
                            e.preventDefault();
                            alert('PayPal integration in development');
                          }}
                        >
                          Pay with PayPal
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="payment-methods-notice">
                    {window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1') ? (
                      <p>Digital payment methods are not available in local development. Visit the live site to use Apple Pay, Google Pay, or PayPal.</p>
                    ) : !window.location.protocol.includes('https') ? (
                      <p>Digital payment methods require a secure connection (https).</p>
                    ) : (
                      <p>Digital payment methods are not available on your current device/browser.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="payment-divider">
                <span>Pay with card</span>
              </div>

              <div className="card-element-wrapper">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                    hidePostalCode: true,
                  }}
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              className="submit-button"
              disabled={!stripe || processing}
              type="submit"
            >
              {processing ? 'Processing...' : isAdmin ? 'Buy Course (Admin Price: $0.51)' : 'Buy Course - $297'}
            </button>

            <div className="secure-payment">
              <span className="lock-icon">🔒</span>
              <span>Secure payment - SSL encrypted</span>
            </div>
          </form>
        )}

        <style>{`
          .payment-wrapper {
            background: rgba(255, 255, 255, 0.95);
            border-radius: 12px;
            padding: 2rem;
            min-width: 100%;
            margin: 0 auto;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          .payment-header {
            text-align: center;
            margin-bottom: 2rem;
          }

          .header-title {
            font-family: "Roboto Slab", serif !important;
            font-size: 2rem !important;
            font-weight: 700 !important;
            color: #1a1a1a;
            margin-bottom: 1rem !important;
          }

          .price-info {
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .price-line {
            font-family: "Roboto", sans-serif !important;
            font-size: 1.25rem !important;
            color: #4a4a4a;
            display: flex;
            gap: 1rem;
            align-items: center;
            justify-content: center;
          }

          .original-price {
            text-decoration: line-through;
            color: #666;
            font-size: 1rem;
          }

          .current-price {
            color: #1a1a1a;
            font-weight: 500;
          }

          .form-row {
            display: flex;
            gap: 1rem;
            margin-bottom: 1rem;
          }

          .form-field {
            margin-bottom: 20px;
          }

          .payment-methods {
            margin-top: 2rem;
          }

          .digital-payment-buttons {
            display: flex;
            gap: 12px;
            margin-bottom: 24px;
          }

          .payment-button-container {
            flex: 1;
            min-width: 160px;
            position: relative;
          }

          .admin-payment-button {
            width: 100%;
            height: 40px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .admin-payment-button.paypal {
            background-color: #0070ba;
            color: white;
          }

          .admin-payment-button:hover {
            opacity: 0.9;
          }

          .admin-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            border-radius: 4px;
          }

          .payment-methods-notice {
            flex: 1;
            padding: 12px;
            background-color: #f8f9fa;
            border-radius: 8px;
            color: #6c757d;
            font-size: 14px;
            text-align: center;
          }

          .submit-button {
            background: linear-gradient(180deg, #ff0000, #cc0000);
            color: white;
            border: none;
            border-radius: 8px;
            padding: 1rem;
            font-size: 1.1rem;
            font-weight: 600;
            width: 100%;
            margin-top: 1.5rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .submit-button:disabled {
            background: #cccccc;
            cursor: not-allowed;
          }

          .error-message {
            color: #e53e3e;
            background: #fff5f5;
            padding: 0.75rem;
            border-radius: 6px;
            margin-top: 1rem;
            font-size: 0.9rem;
          }

          .secure-payment {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 1rem;
            color: #666;
            font-size: 0.9rem;
          }

          .user-info-display {
            background: rgba(0, 0, 0, 0.05);
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
          }

          .MuiInputAdornment-root .MuiIconButton-root {
            color: rgba(0, 0, 0, 0.54);
          }

          .MuiFormHelperText-root {
            margin-top: 4px;
            font-size: 0.75rem;
          }

          /* Ensure Stripe Elements maintain consistent width */
          .ElementsApp {
            width: 100% !important;
          }
        `}</style>
      </div>
      
      {customContent?.badges && customContent.badges.length > 0 && !alreadyOwnsProduct && (
        <div className={classes.badgesSection}>
          {customContent.badges.map((badge, index) => (
            <div key={index} className={classes.badge}>
              {badge.image && <img src={badge.image} alt={badge.title} />}
              <h4>{badge.title}</h4>
              <p>{badge.description}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Move PaymentFormWrapper outside main component
const PaymentFormWrapper = ({ customContent, isAdmin, isTestMode }) => {
  if (!stripePromise) {
    console.error('Stripe key not found');
    return <div>Payment system unavailable</div>;
  }
  
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm 
        customContent={customContent} 
        isAdmin={isAdmin || isTestMode} 
      />
    </Elements>
  );
};

export default function SalesPage({ isPreview = false, customStyles = null, customContent = null }) {
  const [styles, setStyles] = useState(customStyles || defaultStyles);
  const [content, setContent] = useState(customContent || defaultContent);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [alreadyOwnsProduct, setAlreadyOwnsProduct] = useState(false);
  const auth = getAuth();
  const history = useHistory();
  const { userRoles } = useContext(AuthContext);
  
  // Check if user already owns the course
  useEffect(() => {
    const checkCourseOwnership = async () => {
      if (auth.currentUser && !isPreview) {
        try {
          // First check context (faster)
          if (userRoles?.productsOwned?.includes('all-courses')) {
            console.log('User already owns this course (from context)');
            setAlreadyOwnsProduct(true);
            
            // Redirect after a short delay to allow the component to render first
            setTimeout(() => {
              toast('You already own this course. Redirecting to your courses...', { 
                icon: 'ℹ️'
              });
              history.push('/courses');
            }, 1000);
            return;
          }
          
          // Double-check Firestore (in case context is stale)
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.productsOwned && 
                (userData.productsOwned.includes('all-courses') || 
                 (Array.isArray(userData.productsOwned) && 
                  userData.productsOwned.some(product => 
                    typeof product === 'object' && 
                    product.id === 'all-courses'
                  ))
                )
               ) {
              console.log('User already owns this course (from Firestore)');
              setAlreadyOwnsProduct(true);
              
              // Redirect after a short delay
              setTimeout(() => {
                toast('You already own this course. Redirecting to your courses...', { 
                  icon: 'ℹ️'
                });
                history.push('/courses');
              }, 1000);
            }
          }
        } catch (error) {
          console.error('Error checking course ownership:', error);
        }
      }
    };
    
    checkCourseOwnership();
  }, [auth.currentUser, userRoles, history, isPreview]);

  // Update admin check effect to use Firestore
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (auth.currentUser) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          setIsAdmin(userDoc.exists() && userDoc.data().userType === 'admin');
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
    };
    checkAdminStatus();
  }, [auth.currentUser]);

  // Update styles when customStyles prop changes
  useEffect(() => {
    if (customStyles) {
      setStyles(customStyles);
    }
  }, [customStyles]);

  // Update content when customContent prop changes
  useEffect(() => {
    if (customContent) {
      setContent(customContent);
    }
  }, [customContent]);

  const classes = useStyles({ isPreview, customStyles: styles });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = content?.testimonials || defaultTestimonials;

  // Restore testimonial rotation
  useEffect(() => {
    if (testimonials?.length > 0) {
      const rotationDuration = content?.testimonialRotationDuration || 5000; // Default to 5 seconds if not set
      const timer = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, rotationDuration);
      
      return () => clearInterval(timer);
    }
  }, [testimonials, content?.testimonialRotationDuration]);

  useEffect(() => {
    // Only load from Firestore if we're not using custom content/styles
    if (!customContent && !customStyles) {
      loadContent();
      loadStyles();
    }
  }, [customContent, customStyles]);

  const loadContent = async () => {
    try {
      const db = getFirestore();
      // Explicitly load from live version
      const contentDoc = await getDoc(doc(db, 'siteConfig', 'salesPageContent'));
      if (contentDoc.exists()) {
        const savedContent = contentDoc.data();
        console.log('Loaded live content from Firestore:', savedContent);
        setContent({
          ...defaultContent,
          ...savedContent,
          sectionOrder: {
            ...defaultContent.sectionOrder,
            ...(savedContent.sectionOrder || {})
          },
          visibleSections: {
            ...defaultContent.visibleSections,
            ...(savedContent.visibleSections || {})
          }
        });
      }
    } catch (error) {
      console.error('Error loading live sales page content:', error);
    }
  };

  const loadStyles = async () => {
    try {
      const db = getFirestore();
      // Explicitly load from live version
      const stylesDoc = await getDoc(doc(db, 'siteConfig', 'salesPageStyles'));
      if (stylesDoc.exists()) {
        const savedStyles = stylesDoc.data();
        console.log('Loaded live styles from Firestore:', savedStyles);
        setStyles(savedStyles);
      }
    } catch (error) {
      console.error('Error loading live sales page styles:', error);
    }
  };

  const renderPaymentForm = () => {
    return (
      <>
        <PaymentFormWrapper 
          customContent={content} 
          isAdmin={isAdmin} 
          isTestMode={isTestMode}
        />
        <CourseFeatures customContent={content} position="right" />
      </>
    );
  };

  const videoContainerStyle = {
    width: '100%',
    maxWidth: '1002px',
    aspectRatio: '16/9',
    borderRadius: '25px',
    overflow: 'hidden',
    marginTop: '20px',
    marginBottom: '50px',
    position: 'relative',
  };

  const videoOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    display: content?.mediaType === 'video' && content?.thumbnail && !content?.isPlaying ? 'block' : 'none',
  };

  // Add state for video playing
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    if (content?.autoplayVideo) {
      setIsVideoPlaying(true);
    }
  }, [content?.autoplayVideo]);

  const scrollToPayment = () => {
    const paymentElement = document.querySelector('#payment-section');
    if (paymentElement) {
      paymentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderOrderedSections = (side) => {
    const sections = content?.sectionOrder?.[side] || defaultContent.sectionOrder[side];
    console.log(`[DEBUG] Rendering sections for ${side} panel:`, sections);
    console.log(`[DEBUG] Content sectionOrder:`, content?.sectionOrder);
    console.log(`[DEBUG] Content visibleSections:`, content?.visibleSections);
    
    return sections.map((sectionName) => {
      console.log(`[DEBUG] Processing section ${sectionName} for ${side} panel`);
      // Check visibility based on panel side
      const isVisible = content?.visibleSections?.[side]?.[sectionName] ?? defaultContent.visibleSections[side][sectionName];
      console.log(`[DEBUG] Section ${sectionName} visibility for ${side} panel:`, isVisible);
      if (!isVisible) {
        console.log(`[DEBUG] Section ${sectionName} is hidden for ${side} panel`);
        return null;
      }

      const textColor = styles?.textColor || '#ffffff';

      switch (sectionName) {
        case 'title':
          return (
            <React.Fragment key="title">
              {content?.logo?.enabled && content?.logo?.position === 'above' && (
                <img 
                  src={content.logo.url} 
                  alt="Logo"
                  className={classes.logo}
                />
              )}
              <Typography 
                variant="h1" 
                className={classes.mainTitle}
                style={{ color: styles?.textColors?.title || styles?.textColor || '#ffffff' }}
              >
                {content?.title || "Meet Girls Everywhere"}
              </Typography>
              {content?.logo?.enabled && content?.logo?.position === 'between' && (
                <img 
                  src={content.logo.url} 
                  alt="Logo"
                  className={classes.logo}
                />
              )}
              <Typography 
                variant="h2" 
                className={classes.subtitle}
                style={{ color: styles?.textColors?.subtitle || styles?.textColor || '#ffffff' }}
              >
                {content?.subtitle || "20+ Modules to Transform Your Dating Life"}
              </Typography>
              {content?.logo?.enabled && content?.logo?.position === 'below' && (
                <img 
                  src={content.logo.url} 
                  alt="Logo"
                  className={classes.logo}
                />
              )}
            </React.Fragment>
          );
        case 'video':
          return (
            <div key="video" className={classes.previewSection}>
              <div style={videoContainerStyle}>
                {content?.mediaType === 'video' ? (
                  <>
                    <iframe
                      src={getYouTubeEmbedUrl(content?.mediaUrl)}
                      title="Course Preview Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                    />
                  </>
                ) : (
                  <img 
                    src={content?.mediaUrl || "https://cdn.builder.io/api/v1/image/assets/TEMP/0600b6e10a801aedbbafd987da22c68e37c074a0"} 
                    alt="Course Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </div>
            </div>
          );
        case 'features':
          return <CourseFeatures key="features" customContent={content} customStyles={{ ...styles, textColor }} position={side} />;
        case 'testimonials':
          return (
            <div key="testimonials" className={classes.testimonialSection}>
              {content.testimonialSection?.heading && (
                <Typography variant="h2" className={classes.testimonialHeading}>
                  {content.testimonialSection.heading}
                </Typography>
              )}
              
              {content.testimonialSection?.subtext && (
                <Typography variant="body1" className={classes.testimonialSubtext}>
                  {content.testimonialSection.subtext}
                </Typography>
              )}
              
              <div className={classes.testimonialContainer}>
                <div className={classes.testimonialGrid}>
                  {(content?.testimonials || defaultTestimonials).map((testimonial, index) => (
                    <div key={index} className={classes.testimonialVideo}>
                      {testimonial.header && (
                        <Typography variant="h3" className={classes.testimonialHeader}>
                          {testimonial.header}
                        </Typography>
                      )}
                      <div className={classes.videoWrapper}>
                        {testimonial.videoUrl && (
                          testimonial.videoUrl.includes('youtube.com') || testimonial.videoUrl.includes('youtu.be') ? (
                            <iframe
                              src={getYouTubeEmbedUrl(testimonial.videoUrl)}
                              title={`Student Testimonial ${index + 1}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{
                                backgroundImage: testimonial.thumbnail ? `url(${testimonial.thumbnail})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            />
                          ) : (
                            <video
                              src={testimonial.videoUrl}
                              controls
                              poster={testimonial.thumbnail}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )
                        )}
                      </div>
                      {testimonial.subtext && (
                        <Typography variant="body1" className={classes.testimonialSubtext}>
                          {testimonial.subtext}
                        </Typography>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        case 'instructor':
          return <InstructorSection 
            key="instructor" 
            customStyles={{ ...styles, textColor }} 
            customContent={content}
          />;
        case 'description':
          return (
            <div key="description" className={classes.courseDescriptionSection}>
              <div className="main-description">
                <h2 style={{ color: textColor }}>{content?.courseDescription?.mainHeading || "🚀 Transform Your Dating Life"}</h2>
                <p style={{ color: textColor }}>{content?.courseDescription?.overview || "Meet Girls Everywhere – The Ultimate Course to Master Social Dynamics and Attraction: If you're tired of swiping endlessly, feeling stuck in your dating life, or simply not getting the results you want with women, Meet Girls Everywhere is the solution you've been waiting for. This isn't just another course on pickup lines or awkward routines. This is a complete system for understanding women, building your confidence, and mastering the art of attraction in everyday life."}</p>
              </div>
              <div className="what-is">
                <h2 style={{ color: textColor }}>{content?.courseDescription?.whatIs?.heading || "💡 What is \"Meet Girls Everywhere\"?"}</h2>
                <p style={{ color: textColor }}>{content?.courseDescription?.whatIs?.content || "\"Meet Girls Everywhere\" is a deep-dive, no-fluff program created by dating and social dynamics expert Hector Castillo. This step-by-step course guides you through the mindset, behaviors, habits, and skills needed to naturally and confidently meet women—in any situation. Whether you're walking down the street, in a coffee shop, at the gym, or just out with friends, this course teaches you how to turn daily life into opportunity."}</p>
              </div>
              <div className="ready-to-start">
                <h2 style={{ color: textColor }}>{content?.courseDescription?.readyToStart?.heading || "Ready to Get Started"}</h2>
                <p style={{ color: textColor }}>{content?.courseDescription?.readyToStart?.content || "This step-by-step course guides you through the mindset, behaviors, habits, and skills needed to naturally and confidently meet women—in any situation. Whether you're walking down the street, in a coffee shop, at the gym, or just out with friends, this course teaches you how to turn daily life into opportunity."}</p>
              </div>
            </div>
          );
        case 'payment':
          return renderPaymentForm();
        case 'bonuses':
          console.log(`[DEBUG] Rendering bonuses for ${side} panel`);
          console.log(`[DEBUG] Bonuses data:`, content?.bonuses);
          return side === 'right' ? (
            <BonusSection 
              key="bonuses" 
              bonuses={content?.bonuses} 
              compact={true} 
              customStyles={{ textColor }} 
            />
          ) : (
            <FullBonusSection 
              key="bonuses" 
              bonuses={content?.bonuses} 
              customStyles={{ textColor }} 
            />
          );
        default:
          return null;
      }
    }).filter(Boolean);
  };

  // Add development mode test controls
  const renderDevControls = () => {
    if (typeof window !== 'undefined' && window.env?.NODE_ENV !== 'development') return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        background: 'rgba(0,0,0,0.8)',
        padding: '10px',
        borderRadius: '5px',
        zIndex: 9999,
        color: 'white'
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            checked={isTestMode}
            onChange={(e) => setIsTestMode(e.target.checked)}
          />
          Test Mode ($0.51)
        </label>
      </div>
    );
  };

  return (
    <div 
      className={classes.pageWrapper}
    >
      <div className={classes.overlay} />
      <Header
        color="transparent"
        brand={<img src={logo} alt="logo" style={{ width: 'auto', height: '4rem' }} />}
        links={<HeaderLinks dropdownHoverColor="info" />}
        fixed
        changeColorOnScroll={{
          height: 300,
          color: "white"
        }}
      />
      <div className={classes.contentContainer}>
        <div className={classes.checkoutPage}>
          <div className={classes.contentWrapper}>
            <div className={classes.checkoutContainer}>
              <div className={classes.leftPanel}>
                {renderOrderedSections('left')}
              </div>
              <div id="payment-section" className={classes.rightPanel}>
                {renderOrderedSections('right')}
              </div>
            </div>
          </div>

          <div className={classes.mobileCta}>
            <button 
              className={classes.ctaButton}
              onClick={scrollToPayment}
            >
              {isAdmin ? 'Buy Course (Admin Price: $0.51)' : 'Buy Course - $297'}
            </button>
          </div>
        </div>

        <Footer 
          content={
            <div>
              <div className={classes.left}>
                <List className={classes.list}>
                  <ListItem className={classes.inlineBlock}>
                    <a href="/" className={classes.block}>
                      About
                    </a>
                  </ListItem>
                  <ListItem className={classes.inlineBlock}>
                    <a href="/blog-post" className={classes.block}>
                      Blog
                    </a>
                  </ListItem>
                </List>
              </div>
              <div className={classes.right}>
                &copy; {1900 + new Date().getYear()} , made with{' '}
                <Favorite className={classes.icon} /> by{' '}
                <a
                  href="https://www.dsignable.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dsignable
                </a>{' '}
                for a better web
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
} 